import { compareRolls } from '../combat/pipeline';
import { lairAdjacent, movableTroops, neighborsOf, ownedTerritories, tradeValue } from '../selectors';
import type { Rng } from '../rng';
import type { Action, LootKind, PlayerView, TerritoryId } from '../types';
import { bossAttackChoice } from './boss';
import { attackBonus, borderDistance, captainTarget, holdValue, isBorder, leader, occupationTroops, reinforceScore, threat } from './heuristics';

/** Ordem de preferência ao escolher loot do boss (e inverso ao descartar). */
const LOOT_PRIORITY: LootKind[] = [
  'reinforce',
  'strike',
  'barrage',
  'captain',
  'precision',
  'teleport',
  'luck',
  'strength',
  'draw',
  'ward',
  'blessing',
  'steal',
  'paralyze',
  'wall',
  'restore',
];

type A<T extends Action['type']> = Extract<Action, { type: T }>;
const of = <T extends Action['type']>(legal: Action[], type: T) => legal.filter((a) => a.type === type) as A<T>[];

function bestByScore<T>(items: T[], score: (x: T) => number): T | undefined {
  let best: T | undefined;
  let bestScore = -Infinity;
  for (const it of items) {
    const sc = score(it);
    if (sc > bestScore) {
      bestScore = sc;
      best = it;
    }
  }
  return best;
}

interface AttackPlan {
  action: A<'attack'>;
  score: number;
  ratio: number;
}

/** Melhor ataque com proporção de pelo menos 1,5 para 1. */
function bestAttack(v: PlayerView, legal: Action[]): AttackPlan | null {
  const me = v.me!;
  let best: AttackPlan | null = null;
  for (const a of of(legal, 'attack')) {
    if (a.untilEnd) continue;
    const att = v.territories[a.from].troops;
    const def = v.territories[a.to].troops;
    const ratio = att / def;
    if (att < 3 || ratio < 1.5) continue;
    const score = ratio + attackBonus(v, me, a.to);
    if (!best || score > best.score) best = { action: a, score, ratio };
  }
  return best;
}

/** Território próprio mais valioso entre os ameaçados (inimigos vizinhos mais fortes que ele). */
function mostThreatened(v: PlayerView, ts: TerritoryId[]): TerritoryId | undefined {
  const me = v.me!;
  const threatened = ts.filter((t) => threat(v, t) > v.territories[t].troops);
  return bestByScore(threatened, (t) => holdValue(v, me, t) + (threat(v, t) - v.territories[t].troops) * 0.5);
}

export function chooseNormal(v: PlayerView, legal: Action[], rng: Rng): Action {
  const me = v.me!;
  const end = legal.find((a) => a.type === 'endPhase');
  const pending = v.pending;
  const loot = of(legal, 'playLoot');
  const lootOf = (k: LootKind) => loot.filter((a) => a.kind === k);

  if (pending) {
    switch (pending.kind) {
      case 'conquest': {
        const fromThreat = threat(v, pending.from);
        const toThreat = threat(v, pending.to);
        const ratio = fromThreat === 0 ? 1 : toThreat / (toThreat + fromThreat);
        const want = Math.round(pending.max * Math.max(ratio, 0.5));
        let count = Math.max(pending.min, Math.min(pending.max, want));
        // Ocupação: deixar pelo menos N tropas nos dois lados, quando der.
        const need = occupationTroops(v);
        if (need > 1) {
          const total = pending.max + 1;
          const lo = Math.min(need, pending.max);
          const hi = Math.max(lo, total - need);
          count = Math.max(lo, Math.min(hi, count));
        }
        return of(legal, 'moveIn').find((a) => a.count === count) ?? legal[legal.length - 1];
      }
      case 'lootChoice': {
        const opts = of(legal, 'chooseLoot');
        for (const k of LOOT_PRIORITY) {
          const hit = opts.find((a) => a.kind === k);
          if (hit) return hit;
        }
        return opts[0];
      }
      case 'bossRegion': {
        const target = leader(v, me);
        return (
          bestByScore(of(legal, 'chooseBossRegion'), (a) =>
            lairAdjacent(v, a.region).reduce((sum, t) => sum + (v.territories[t].owner === target ? 2 : 0.5), 0),
          ) ?? legal[0]
        );
      }
      case 'reaction': {
        const c = pending.combat;
        const out = compareRolls(c.attackRolls, c.defenseRolls, c.tieGoesTo, c.defenderWinsAll);
        const lost = pending.role === 'attacker' ? out.attackerLoss : out.defenderLoss;
        return legal.find((a) => a.type === 'react' && a.use === lost >= 2) ?? legal[0];
      }
      case 'lootOverflow': {
        // Joga uma carta útil agora; senão descarta a menos valiosa.
        const useful = ['reinforce', 'draw', 'strike', 'strength'] as LootKind[];
        for (const k of useful) {
          const play = lootOf(k)[0];
          if (play) return play;
        }
        const discards = of(legal, 'discardLoot');
        return bestByScore(discards, (a) => LOOT_PRIORITY.indexOf(a.kind)) ?? legal[0];
      }
      case 'heal':
        return bestByScore(of(legal, 'heal'), (a) => reinforceScore(v, me, a.territory)) ?? legal[0];
      case 'hidden':
        return legal[0];
    }
  }

  if (v.phase === 'setup') {
    const places = of(legal, 'place');
    const max = Math.max(...places.map((a) => a.count));
    const best = bestTerritory(v, places.filter((a) => a.count === max).map((a) => a.territory));
    return places.find((a) => a.count === max && a.territory === best) ?? legal[0];
  }

  // Loot de efeito imediato, a qualquer momento do turno
  if (lootOf('reinforce')[0] && v.phase !== 'fortify') return lootOf('reinforce')[0];
  if (lootOf('draw')[0]) return lootOf('draw')[0];
  const steal = bestByScore(lootOf('steal'), (a) => v.players[a.victim!].cardCount);
  if (steal) return steal;
  const restore = bestByScore(lootOf('restore'), (a) => threat(v, a.territory!) - v.territories[a.territory!].troops);
  if (restore) return restore;

  if (v.phase === 'reinforce') {
    const trades = of(legal, 'trade');
    if (trades.length) {
      const forced = legal.every((a) => a.type === 'trade');
      if (forced || tradeValue(v.info.ruleset, v.tradeCount) >= 8) return trades[0];
    }
    const places = of(legal, 'place');
    if (places.length) return placeChunk(v, places);
    return end ?? legal[0];
  }

  // Tropas extras (Contrato de Mercenários) fora da fase: colocar
  const places = of(legal, 'place');
  if (places.length) return placeChunk(v, places);

  if (v.phase === 'attack') {
    // Boss: último golpe, fechar um par de dano (carta) ou impedir uma raid em cima de mim
    const bossAttack = bossAttackChoice(v, of(legal, 'attackBoss'));
    if (bossAttack) return bossAttack;
    // Passagem do Capitão: só quando o ataque de porto a porto vale a pena
    const captain = lootOf('captain')[0];
    if (captain && captainTarget(v)) return captain;
    const activeCaptain = v.effects.find((e) => e.kind === 'captain' && e.owner === me && !e.link);
    const capTarget = activeCaptain ? captainTarget(v) : null;
    if (capTarget) {
      const go = of(legal, 'attack').find((x) => x.from === capTarget.from && x.to === capTarget.to && x.untilEnd);
      if (go) return go;
    }
    const plan = bestAttack(v, legal);
    // Runas antes de atacar, no alvo que mais aumenta a chance de conquista
    const strikeTargets = lootOf('strike');
    if (strikeTargets.length) {
      const target = plan?.action.to;
      const pick =
        strikeTargets.find((a) => a.territory === target) ??
        bestByScore(strikeTargets, (a) => attackBonus(v, me, a.territory!) - v.territories[a.territory!].troops);
      if (pick && (target === pick.territory || v.territories[pick.territory!].troops <= 3)) return pick;
    }
    const barrage = bestByScore(lootOf('barrage'), (a) =>
      a.targets!.reduce((sum, t) => sum + (t === plan?.action.to ? 3 : 1), 0),
    );
    if (barrage && barrage.targets!.length >= 2) return barrage;
    if (plan) {
      const { action, ratio } = plan;
      const power = legal.find((a) => a.type === 'power' && a.target === action.to);
      if (power) return power;
      // Monk: guarda harmonia até o máximo, ou solta antes de um ataque difícil.
      const harmony = legal.find((a) => a.type === 'harmony');
      if (harmony && (v.players[me].harmony >= v.info.ruleset.harmonyMax || ratio < 2)) return harmony;
      if (lootOf('strength')[0]) return lootOf('strength')[0];
      if (lootOf('precision')[0]) return lootOf('precision')[0];
      if (ratio >= 2.5) {
        const blitz = of(legal, 'attack').find((a) => a.untilEnd && a.from === action.from && a.to === action.to);
        if (blitz) return blitz;
      }
      return action;
    }
    return end ?? legal[0];
  }

  if (v.phase === 'fortify') {
    // Defesa antes de passar a vez
    const mine = ownedTerritories(v, me);
    const danger = mostThreatened(v, mine);
    if (danger) {
      const ward = lootOf('ward').find((a) => a.territory === danger);
      if (ward) return ward;
      const bless = lootOf('blessing').find((a) => a.territory === danger);
      if (bless) return bless;
      const strongest = bestByScore(
        neighborsOf(v, danger).filter((n) => v.territories[n].owner !== me),
        (n) => v.territories[n].troops,
      );
      const wall = lootOf('wall').find((a) => (a.from === danger && a.to === strongest) || (a.to === danger && a.from === strongest));
      if (wall) return wall;
    }
    // Paralisar o maior exército inimigo da fronteira
    const para = bestByScore(
      lootOf('paralyze').filter((a) => neighborsOf(v, a.territory!).some((n) => v.territories[n].owner === me)),
      (a) => v.territories[a.territory!].troops,
    );
    if (para && v.territories[para.territory!].troops >= 4) return para;
    const teleports = lootOf('teleport');
    if (teleports.length) {
      const dist = borderDistance(v, me);
      const good = teleports.filter(
        (a) => (dist.get(a.from!) ?? 0) > 0 && dist.get(a.to!) === 0 && a.count === movableTroops(v, a.from!),
      );
      const pick = bestByScore(good, (a) => a.count! + threat(v, a.to!) * 0.1);
      if (pick && pick.count! >= 3) return pick;
    }
    const move = fortifyMove(v, legal);
    if (move) return move;
    return end ?? legal[0];
  }

  return end ?? legal[rng.int(legal.length)];
}

function bestTerritory(v: PlayerView, ts: TerritoryId[]): TerritoryId {
  return bestByScore(ts, (t) => reinforceScore(v, v.me!, t) + holdValue(v, v.me!, t) * 0.3) ?? ts[0];
}

/** Coloca reforços em blocos no território mais valioso. */
function placeChunk(v: PlayerView, places: A<'place'>[]): Action {
  const total = Math.max(...places.map((a) => a.count));
  const territory = bestTerritory(v, [...new Set(places.map((a) => a.territory))]);
  const count = total > 4 ? Math.ceil(total / 2) : total;
  return places.find((a) => a.territory === territory && a.count === count) ?? places[0];
}

/** Leva tropas do interior para a fronteira. */
function fortifyMove(v: PlayerView, legal: Action[]): Action | null {
  const me = v.me!;
  // Ocupação: completar primeiro os territórios com menos tropas que o exigido.
  const need = occupationTroops(v);
  if (need > 1) {
    const fill = of(legal, 'fortify').find((a) => {
      const to = v.territories[a.to].troops;
      const from = v.territories[a.from].troops;
      return to < need && a.count === need - to && from - a.count >= need;
    });
    if (fill) return fill;
  }
  const dist = borderDistance(v, me);
  let best: A<'fortify'> | null = null;
  let bestCount = 0;
  for (const a of of(legal, 'fortify')) {
    const df = dist.get(a.from);
    const dt = dist.get(a.to);
    if (df === undefined || df === 0) continue; // a origem já é fronteira
    if (dt === undefined || dt >= df) continue;
    if (a.count !== movableTroops(v, a.from)) continue;
    if (a.count > bestCount) {
      bestCount = a.count;
      best = a;
    }
  }
  if (best) return best;
  // Sem interior: reforçar a fronteira mais ameaçada a partir de uma vizinha calma.
  for (const a of of(legal, 'fortify')) {
    if (a.count !== movableTroops(v, a.from) || a.count < 3) continue;
    if (!isBorder(v, a.to)) continue;
    if (threat(v, a.to) > v.territories[a.to].troops * 2 && threat(v, a.from) < v.territories[a.from].troops) return a;
  }
  return null;
}
